import { useEffect, useRef } from 'react';
import { getColor } from '@/helpers/utils';
import { trackerTooltips } from '@/app/KeywordTracker/tooltip/trackerTooltips';
import { array, number, object, string } from 'yup';
import { Formik } from 'formik';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { errorMessage, successMessage, warningMessage } from '@/utils/toast';
import { FaMobileScreenButton } from 'react-icons/fa6';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';
import keywordsApi, {
  useCreateKeywordsMutation,
  useGetLanguagesForSelectQuery,
  useGetTariffInfoQuery
} from '@/app/KeywordTracker/api/keywordsApi';
import { useGetLocationsForSelectQuery } from '@/app/KeywordTracker/api/locationsApi';

import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useNavigate } from 'react-router-dom';
import OptionLayoutLanguageTracker from '@/app/common/components/OptionsLayout/OptionLayoutLanguageTracker';
import OptionLayoutLocationTracker from '@/app/common/components/OptionsLayout/OptionLayoutLocationTracker';
import classNames from 'classnames';
import _ from 'lodash';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import AdvancedAddKeywordsSetting from './AdvancedAddKeywordsSetting';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import SubtleBadge from '@/components/common/SubtleBadge';
import Select from 'react-select';
import type { GroupBase } from 'react-select';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import useDomain from '@/hooks/useDomain';
import { addTransaction } from '@/app/common/components/ag-grid/addTransaction';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import type { AddKeywordsFormValues, GridRef, SelectOption } from './types';

let initialLimit = 10;

const addKeywordsInitialValues: AddKeywordsFormValues = {
  keywords: [],
  device_type: 1,
  group_id: null,
  landing_url_id: null,
  tag_ids: [],
  language_code: null,
  location_coordinate_id: null
};

interface AddKeywordsDrawerContentProps {
  limitKeywords: number;
  setLimitKeywords: Dispatch<SetStateAction<number>>;
  isMemberStep?: boolean;
  domain_slug?: string;
  setShow?: (show: boolean) => void;
  setShouldRevalidateFirstPage?: ((value: boolean) => void) | null;
  gridRef?: GridRef | null;
  initialLimit?: number;
}

const AddKeywordsDrawerContent = ({
  limitKeywords,
  setLimitKeywords,
  isMemberStep = false,
  domain_slug: domainSlugProp,
  setShow = () => {},
  setShouldRevalidateFirstPage = null,
  gridRef = null
}: AddKeywordsDrawerContentProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: userInfo } =
    useGetUserInfoQuery();
  const { domain_slug: domainSlugFromHook } = useDomain();
  const domain_slug = domainSlugProp ?? domainSlugFromHook ?? undefined;
  // if (!userInfo) return '';
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug, { skip: !userInfo });

  const { data: geoLanguagesData, isLoading: isLoadingLanguages } =
    useGetLanguagesForSelectQuery(undefined);
  const { data: geoLocationsData, isLoading: isLoadingLocations } =
    useGetLocationsForSelectQuery(undefined);
  const [createKeywords] = useCreateKeywordsMutation();
  useEffect(() => {
    const tariffKeywordLimit = trackerInfo?.data?.tariff?.num_of_keywords;
    const usedKeywordCount = trackerInfo?.data?.used?.num_of_keywords;

    initialLimit =
      typeof tariffKeywordLimit === 'number' &&
      typeof usedKeywordCount === 'number'
        ? tariffKeywordLimit - usedKeywordCount
        : 10;
    setLimitKeywords(initialLimit);
  }, [isFetchingTrackerInfo]);
  const lineNumbersRef = useRef<HTMLTextAreaElement>(null);
  const keywordsRef = useRef<HTMLTextAreaElement>(null);

  const handleScroll = () => {
    // هماهنگ کردن اسکرول لیست اعداد و textarea
    if (lineNumbersRef?.current && keywordsRef?.current) {
      lineNumbersRef.current.scrollTop = keywordsRef.current.scrollTop;
    }
  };

  // Handle Device Type
  const deviceChangeHanlder = async (
    value: number | null,
    setFieldValue: (field: string, val: unknown) => Promise<unknown>,
    e: ChangeEvent<HTMLInputElement>,
    validateField: (field: string) => Promise<unknown>
  ) => {
    const newDeviceType = Number(e.target.value);
    try {
      if (value === 1 || value === 2) {
        if (newDeviceType === 1) {
          setLimitKeywords(value === 1 ? initialLimit : initialLimit / 2);
          await setFieldValue('device_type', value === 1 ? null : 3);
        } else if (newDeviceType === 2) {
          setLimitKeywords(value === 1 ? initialLimit / 2 : initialLimit);
          await setFieldValue('device_type', value === 1 ? 3 : null);
        }
      } else if (value === 3) {
        await setFieldValue('device_type', newDeviceType === 1 ? 2 : 1);
        await setLimitKeywords(initialLimit);
      } else if (value === null) {
        setLimitKeywords(initialLimit);
        await setFieldValue('device_type', newDeviceType);
      }
      await validateField('keywords');
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    const setupObserver = () => {
      const lineNumbersEl = lineNumbersRef.current;
      const keywordsEl = keywordsRef.current;

      if (lineNumbersEl && keywordsEl) {
        const resizeObserver = new ResizeObserver(() => {
          if (lineNumbersEl.style?.height)
            lineNumbersEl.style.height = `${keywordsEl.offsetHeight}px`;
        });
        resizeObserver.observe(keywordsEl);

        return () => {
          resizeObserver.disconnect();
        };
      }
    };

    const timer = setTimeout(setupObserver, 0);
    return () => clearTimeout(timer);
  }, [lineNumbersRef?.current, keywordsRef?.current]);
  return (
    <Formik<AddKeywordsFormValues>
      validationSchema={object().shape({
        keywords: array()
          .of(string()) // Ensure strings are not just whitespace
          .test('not-empty', 'کلمات رو وارد کنید.', value => {
            if (value === undefined || value.length === 0) return false;
            if (
              value.length === 1 &&
              (value[0] === undefined ||
                value[0] === null ||
                value[0].trim() === '')
            ) {
              return false;
            }
            return true;
          })
          .test('not-short', 'کلمه ای نباید تک حرفی باشد.', keywords => {
            if (!keywords) return true;
            return !keywords.some(keyword => keyword?.trim().length === 1);
          })
          .max(limitKeywords, 'تعداد کلمات بیش از حد مجاز است.'),
        location_coordinate_id: object().required('مکان رو انتخاب کنید.'),
        language_code: object().required('زبان رو انتخاب کنید.'),
        tag_ids: array().nullable(),
        device_type: number().required('نوع دستگاه رو انتخاب کنید.'),
        landing_url_id: object().nullable(),
        group_id: object().nullable()
      })}
      onSubmit={async (values: AddKeywordsFormValues) => {
        if (!values.location_coordinate_id) return;

        createKeywords({
          domain_slug,
          keywords: [
            ...new Set(
              values.keywords
                ?.filter(x => x)
                ?.map(x => x.trim())
                ?.filter(word => word !== '')
            )
          ],
          language_code: values.language_code?.value,
          group_id: values.group_id?.value,
          landing_url_id: values.landing_url_id?.value,
          device_type: values.device_type,
          tag_ids: values.tag_ids.map(x => x.value),
          location_coordinate_id: values.location_coordinate_id.value
        })
          .unwrap()
          .then(response => {
            if (
              response?.data?.duplicate_keywords?.length === 0 &&
              response?.data?.long_keywords?.length === 0
            ) {
              if (gridRef?.current) {
                addTransaction(response?.data?.added_keywords, gridRef);
                setShouldRevalidateFirstPage?.(true);
              }
              successMessage('کلمات با موفقیت اضافه شد');
            } else {
              response?.data?.duplicate_keywords?.forEach(
                ({ title }: { title: string }) => {
                  warningMessage(
                    `کلمه (${title}) به دلیل تکراری بودن اضافه نشد.`
                  );
                }
              );
              response?.data?.long_keywords?.forEach((title: string) => {
                warningMessage(
                  `کلمه (${title}) به دلیل طولانی بودن اضافه نشد.`
                );
              });
            }
            if (values.group_id)
              dispatch(
                keywordsApi.util.invalidateTags(['groups', 'groupsForSelect'])
              );
            if (values.landing_url_id)
              dispatch(
                keywordsApi.util.invalidateTags([
                  'landing_urls',
                  'landingUrlsForSelect'
                ])
              );
            if (values.tag_ids.length > 0)
              dispatch(
                keywordsApi.util.invalidateTags(['tags', 'tagsForSelect'])
              );
          })
          .catch(err => {
            errorMessage(
              err?.data?.error || 'خطایی در ایجاد کلمات وجود داره'
            );
            console.error(err);
          });
        isMemberStep ? navigate('/') : setShow(false);
      }}
      initialValues={addKeywordsInitialValues}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleSubmit,
        setFieldValue,
        validateField
      }) => (
        <Form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
          className="d-flex text-start flex-column h-100 justify-content-between"
        >
          <div>
            {/* فرم وارد کردن کلمات */}
            <div className="mt-3 mb-2">
              هر کلمه رو در یک
              <span className="fw-bold"> خط جدید </span>
              بنویس.
              {/* <HelpTooltip html={keywordResearchTooltips.massive.helper} /> */}
            </div>
            <div className="d-flex fs--1 flex-column">
              <div className="d-flex">
                {/* Line numbers column */}
                <div className="p-0" style={{ width: '52px' }}>
                  <Form.Control
                    className="rounded-end-0"
                    ref={lineNumbersRef}
                    as="textarea"
                    disabled
                    value={values.keywords.map((_, i) => `${i + 1}`).join('\n')}
                    rows={5}
                    style={{
                      paddingLeft: 0,
                      paddingRight: 0,
                      resize: 'none',
                      textAlign: 'center',
                      overflow: 'hidden'
                    }}
                  />
                </div>

                {/* Keywords textarea */}
                <Form.Control
                  isInvalid={!!(touched.keywords && errors.keywords)}
                  isValid={!!(touched.keywords && !errors.keywords)}
                  as="textarea"
                  onScroll={handleScroll}
                  ref={keywordsRef}
                  rows={5}
                  className="rounded-start-0"
                  name="keywords"
                  onBlur={handleBlur}
                  onChange={e => {
                    setFieldValue('keywords', e.target.value.split('\n'));
                  }}
                  value={values.keywords.join('\n')}
                  placeholder={`کلمه کلیدی ۱\nکلمه کلیدی ۲\nکلمه کلیدی ۳\n...`}
                />
              </div>

              <div className="my-2 text-nowrap d-flex gap-1 align-items-center">
                {values.keywords.filter(word => word !== '').length <=
                limitKeywords ? (
                  <>
                    <SubtleBadge
                      bg={
                        values.keywords.filter(word => word !== '').length <=
                        Math.floor(limitKeywords / 2)
                          ? 'success'
                          : 'warning'
                      }
                      className="fs--2"
                    >
                      {values.device_type === 3
                        ? (
                            (limitKeywords -
                              values.keywords.filter(word => word !== '')
                                .length) *
                            2
                          ).toFixed(0)
                        : (
                            limitKeywords -
                            values.keywords.filter(word => word !== '').length
                          ).toFixed(0)}
                    </SubtleBadge>
                    &nbsp; کلمه دیگه میتونی وارد کنی
                    <HelpTooltip
                      html={`<span class="mx-1 fw-bolder">${trackerInfo?.data?.used?.num_of_keywords ?? ''}</span> کلمه از <span class="mx-1 fw-bolder">${trackerInfo?.data?.tariff?.num_of_keywords ?? ''}</span> قبلا اضافه شده`}
                    />
                  </>
                ) : (
                  <>
                    <SubtleBadge bg={'danger'} className="fs--2">
                      {values.device_type === 3
                        ? (
                            (limitKeywords -
                              values.keywords.filter(word => word !== '')
                                .length) *
                            -2
                          ).toFixed(0)
                        : (
                            limitKeywords -
                            values.keywords.filter(word => word !== '').length *
                              -1
                          ).toFixed(0)}
                    </SubtleBadge>
                    &nbsp; {errors.keywords}
                    <FontAwesomeIcon
                      icon="exclamation-circle"
                      className="text-danger"
                    />
                  </>
                )}

                {touched.keywords &&
                errors.keywords &&
                values.keywords.filter(word => word !== '').length <
                  limitKeywords ? (
                  <Form.Control.Feedback
                    className="d-inline-block fs--1"
                    type={errors.keywords ? 'invalid' : 'valid'}
                  >
                    {touched.keywords && errors.keywords}
                  </Form.Control.Feedback>
                ) : (
                  <Form.Control.Feedback className="text-danger">
                    {' '}
                  </Form.Control.Feedback>
                )}
              </div>
              <Form.Label className="text-secondary fs--2">
                &nbsp;
                {values.device_type === 3 && (
                  <>
                    * وقتی هر دو دستگاه رو انتخاب کنید هر کلمه دو بار در پنل
                    اضافه میشه.
                  </>
                )}
              </Form.Label>
            </div>

            {/* انتخاب کننده زمان */}
            <div className="my-2">
              <Form.Label className="d-flex align-items-center gap-1">
                مکان و زبان
              </Form.Label>
              <Row className="g-3">
                <Col>
                  <ChildrensTooltip
                    delayToRemove={0}
                    showOnHover={false}
                    placement="top"
                    html=""
                  >
                    {!isLoadingLocations ? (
                      <Select
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        maxMenuHeight={200}
                        isSearchable={true}
                        placeholder="انتخاب مکان"
                        onChange={(e: SelectOption | null) => {
                          if (!e) return;

                          (async () => {
                            await setFieldValue('location_coordinate_id', e);
                            const selectedLocation = geoLocationsData?.data?.find(
                              (item: SelectOption) => item.value === e.value
                            );
                            if (!selectedLocation) return;

                            const { default_language_code } = selectedLocation;

                            let defaultLang: SelectOption = {
                              value: 'en',
                              label: 'انگلیسی'
                            };
                            if (default_language_code) {
                              const matchedLang = geoLanguagesData?.data?.find(
                                (item: SelectOption) =>
                                  item.value === default_language_code
                              );
                              if (matchedLang) defaultLang = matchedLang;
                            }

                            await setFieldValue('language_code', {
                              value: defaultLang.value,
                              label: defaultLang.label
                            });
                            await validateField('location_coordinate_id');
                          })();
                        }}
                        className={isFetchingTrackerInfo ? 'disabled-link' : undefined}
                        options={groupByCountryType(geoLocationsData?.data)}
                        formatGroupLabel={data => (
                          <div className="d-flex gap-1 align-items-center">
                            <span className="my-2 fs--1 text-1100">
                              {data.label}
                            </span>
                            {data?.options[0]?.value !== 'x' ? (
                              <SubtleBadge className="fs--2" bg="info">
                                {data.options.length - 1}
                              </SubtleBadge>
                            ) : (
                              <HelpTooltip html="اگر مکان مدنظر شما در لیست میزفا تولز نبود کافی هست در چت آنلاین درخواست مکان جدید رو رو اعلام کنید تا به ابزار اضافه کنیم." />
                            )}
                          </div>
                        )}
                        value={values.location_coordinate_id}
                        styles={{
                          group: (baseStyles, state) => ({
                            ...baseStyles,
                            backgroundColor: getColor('gray-300'),
                            padding: 0
                          }),
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            paddingLeft: '0px',
                            paddingRight: '10px',
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important',
                            cursor: 'pointer',
                            width: '100% !important',
                            borderRadius: '5px !important'
                          }),
                          input: (baseStyles, state) => ({
                            ...baseStyles,
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important'
                          })
                        }}
                        components={{
                          Option: OptionLayoutLocationTracker
                          // SingleValue: SingleValueFlag
                        }}
                        classNamePrefix="react-select"
                      />
                    ) : (
                      <TextLoading width={100} height={37} />
                    )}
                  </ChildrensTooltip>
                  {touched.location_coordinate_id &&
                  errors.location_coordinate_id ? (
                    <Form.Control.Feedback
                      className="d-block"
                      type={errors.location_coordinate_id ? 'invalid' : 'valid'}
                    >
                      {touched.location_coordinate_id &&
                        errors.location_coordinate_id}
                    </Form.Control.Feedback>
                  ) : (
                    <Form.Label className="text-danger"> </Form.Label>
                  )}
                </Col>
                <Col>
                  <ChildrensTooltip
                    delayToRemove={0}
                    showOnHover={false}
                    placement="top"
                    html=""
                  >
                    {!isLoadingLanguages ? (
                      <Select
                        className={isFetchingTrackerInfo ? 'disabled-link' : undefined}
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        isSearchable={true}
                        placeholder="انتخاب زبان"
                        onChange={e => setFieldValue('language_code', e)}
                        options={geoLanguagesData?.data}
                        classNamePrefix="react-select"
                        value={values.language_code}
                        maxMenuHeight={200}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            paddingLeft: '0px',
                            paddingRight: '10px',
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important',
                            cursor: 'pointer',
                            width: '100% !important',
                            borderRadius: '5px !important'
                          }),
                          input: (baseStyles, state) => ({
                            ...baseStyles,
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important'
                          })
                        }}
                        components={{
                          Option: OptionLayoutLanguageTracker,
                          SingleValue: SingleValueFlag
                        }}
                      />
                    ) : (
                      <TextLoading width={100} height={37} />
                    )}
                  </ChildrensTooltip>

                  {touched.language_code && errors.language_code ? (
                    <Form.Control.Feedback
                      className="d-block"
                      type={errors.language_code ? 'invalid' : 'valid'}
                    >
                      {touched.language_code && errors.language_code}
                    </Form.Control.Feedback>
                  ) : (
                    <Form.Label className="text-danger"> </Form.Label>
                  )}
                </Col>
              </Row>
            </div>
            {/* انتخاب نوع دستگاه */}

            <Col xs={12} className="d-flex align-items-center gap-3">
              <Form.Label className="d-flex align-items-center gap-1">
                نوع دستگاه
                <ChildrensTooltip
                  delayToRemove={0}
                  showOnHover={false}
                  placement="top"
                  html={trackerTooltips.addKeywords.deviceType}
                >
                  <FontAwesomeIcon
                    icon="question-circle"
                    className="ms-1 cursor-pointer"
                    transform="shrink-1"
                  />
                </ChildrensTooltip>
              </Form.Label>
              <div>
                <ChildrensTooltip
                  delayToRemove={0}
                  showOnHover={false}
                  placement="top"
                  html=""
                >
                  <Form.Check
                    className={classNames('my-0 border-0 ', {
                      'disabled-link': isFetchingTrackerInfo
                    })}
                    inline
                    label={
                      <span className="cursor-pointer">
                        <FontAwesomeIcon size="lg" icon="desktop" />
                      </span>
                    }
                    name="desktop-checkbox"
                    id="desktop-checkbox"
                    type="checkbox"
                    checked={
                      values.device_type === 1 || values.device_type === 3
                    }
                    value={1}
                    onChange={e => {
                      deviceChangeHanlder(
                        values.device_type,
                        setFieldValue,
                        e,
                        validateField
                      );
                    }}
                  />
                </ChildrensTooltip>
                <ChildrensTooltip
                  delayToRemove={0}
                  showOnHover={false}
                  placement="top"
                  html=""
                >
                  <Form.Check
                    className={classNames('my-0 border-0 ', {
                      'disabled-link': isFetchingTrackerInfo
                    })}
                    inline
                    label={
                      <span className="cursor-pointer">
                        <FaMobileScreenButton />
                      </span>
                    }
                    name="mobile-checkbox"
                    id="mobile-checkbox"
                    type="checkbox"
                    checked={
                      values.device_type === 2 || values.device_type === 3
                    }
                    value={2}
                    onChange={e => {
                      deviceChangeHanlder(
                        values.device_type,
                        setFieldValue,
                        e,
                        validateField
                      );
                    }}
                  />
                </ChildrensTooltip>
                {touched.device_type && errors.device_type ? (
                  <Form.Label className="text-danger">
                    {errors.device_type}
                  </Form.Label>
                ) : (
                  <Form.Label className="text-danger"> </Form.Label>
                )}
              </div>
            </Col>
            {/* onClick={() => {
                    setLimitKeywords(initialLimit);
                    setFieldValue('device_type', 1).then(() => {
                      validateField('keywords');
                    });
                  }} */}

            {/* setLimitKeywords(initialLimit);
                    setFieldValue('device_type', 2).then(() => {
                      validateField('keywords'); 
                  // onClick={() => {
                  //   setLimitKeywords(initialLimit / 2);
                  //   setFieldValue('device_type', 3).then(() => {
                  //     validateField('keywords');
                  //   });
                  // }}
                  */}

            {/* ADVANCED FEATURES */}
            {!isMemberStep && (
              <AdvancedAddKeywordsSetting
                values={values}
                setFieldValue={setFieldValue}
                domain_slug={domain_slug}
              />
            )}
          </div>
          <div>
            {/* دکمه های آخر صفحه */}

            <div className="position-sm-absolute bottom-0 d-flex gap-2 end-0 justify-content-end p-sm-4 pb-7 pt-2">
              {!isMemberStep && (
                <Button
                  className="d-flex gap-2 align-items-center"
                  variant="outline-secondary"
                  onClick={() => setShow(false)}
                >
                  <FontAwesomeIcon size="lg" icon="times" />
                  لغو
                </Button>
              )}
              <Button
                // disabled={
                //   values.keywords.filter(word => word !== '').length ===
                //     0 ||
                //   values.keywords.filter(word => word !== '').length >
                //     limitKeywords ||
                //   !values.language_code ||
                //   !values.location_coordinate_id
                // }
                className="d-flex gap-2 align-items-center border-0"
                type="submit"
                variant="falcon-primary"
              >
                <FontAwesomeIcon size="lg" icon="plus" />
                {isMemberStep ? 'افزودن و ورود به داشبورد' : 'اعمال کن'}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AddKeywordsDrawerContent;

const groupByCountryType = (
  data: SelectOption[] | undefined
): GroupBase<SelectOption>[] => {
  if (!data?.length) return [];

  const groups: Record<1 | 2, SelectOption[]> = {
    1: [],
    2: []
  };

  data.forEach(item => {
    const countryType = Number(item.country_type);
    if (item.label?.trim() === 'ایران') return;
    if (countryType === 1 || countryType === 2) {
      groups[countryType].push({
        label: item.label.trim(),
        value: item.value
      });
    }
  });

  const iranItem = data.find(item => item.label?.trim() === 'ایران');
  const iranOptions: SelectOption[] = [
    ...(iranItem ? [{ label: 'کل ایران', value: iranItem.value }] : []),
    ...groups[1]
  ];

  return [
    {
      label: 'شهرهای ایران',
      options: iranOptions
    },
    {
      label: 'سایر کشورها',
      options: groups[2]
    },
    {
      label: 'مکان دلخواه',
      options: [{ label: 'کشور (شهر دلخواه)', value: 'x', isDisabled: true }]
    }
  ];
};
